/**
 * Pure logic for mix block suggestion candidates.
 * Filtros (origen, puerto, proveedor, load_type) se derivan siempre del primer ítem del contenedor, no del bloque.
 * Usado por MixBlockSuggestionsPanel en QuotationBlocksView.
 */

import type { SourcingItem } from '@/lib/store-types';
import { isLoadingMethodPalletized, isContainerMoq } from '@/lib/constants';

export interface MoqUnitLike {
  id: string;
  name: string;
}

export interface LoadingMethodLike {
  id: string;
  name: string;
}

/**
 * Returns sourcing items that are valid candidates to add to a mix block.
 * Filters are derived from the **first item** in the block (origin, port, supplier, load type from that product).
 *
 * - When firstItem is null (empty block), returns [] (no suggestions).
 * - Excludes items already in the block (orderIds).
 * - Excludes items whose MOQ unit is full container (Contenedor 20/40).
 * - Includes any item whose MOQ unit is not full container (caja, pallet, unidad, etc.) so familias como suplementos/vitaminas con MOQ por unidad aparezcan.
 * - Same origin_id, origin_port_id as firstItem.
 * - Same supplier: when firstItem has supplier_id (or fallbackSupplierId from block), only same supplier_id; when both empty, only items with no supplier (never mix suppliers).
 * - Same load_type as first item: palletized items only with palletized block, floor/boxes only with floor block.
 * - When family filter is provided: item's family must be the same as first item's or in mix options (allowedFamilyIds).
 *
 * @param fallbackSupplierId - When firstItem.supplier_id is empty, use this (e.g. block.supplier_id from when the block was created) so suggestions match the block's supplier.
 * @param options - Optional: itemIdToFamilyId (map item id -> family id) and allowedFamilyIds (set of family ids that can be mixed with first item's family). When both provided, only items whose family is in allowedFamilyIds are returned.
 */
export function getMixBlockSuggestionCandidates(
  firstItem: SourcingItem | null,
  orderIds: string[],
  allItems: SourcingItem[],
  moqUnits: MoqUnitLike[],
  loadingMethods: LoadingMethodLike[] = [],
  fallbackSupplierId?: string | null,
  options?: { itemIdToFamilyId?: Record<string, string>; allowedFamilyIds?: Set<string> }
): SourcingItem[] {
  if (firstItem == null) return [];

  const orderSet = new Set(orderIds);
  const mixOriginId = String(firstItem.origin_id ?? '');
  const mixPortId = String(firstItem.origin_port_id ?? '');
  const mixSupplierId = String(firstItem.supplier_id ?? fallbackSupplierId ?? '');
  const firstLoadingMethodName = firstItem.loading_method_id
    ? loadingMethods.find((m) => m.id === firstItem.loading_method_id)?.name ?? ''
    : '';
  const blockIsPalletized = isLoadingMethodPalletized(firstLoadingMethodName);
  const applyFamilyFilter = options?.itemIdToFamilyId != null && options?.allowedFamilyIds != null;

  return allItems.filter((item) => {
    if (orderSet.has(item.id)) return false;
    const moqName = moqUnits.find((m) => m.id === item.moq_unit_id)?.name ?? '';
    if (isContainerMoq(moqName)) return false;
    if (String(item.origin_id ?? '') !== mixOriginId || String(item.origin_port_id ?? '') !== mixPortId) return false;
    const itemSupplierId = String(item.supplier_id ?? '');
    if (mixSupplierId !== '') {
      if (itemSupplierId !== mixSupplierId) return false;
    } else {
      if (itemSupplierId !== '') return false;
    }
    const itemLoadingMethodName = item.loading_method_id
      ? loadingMethods.find((m) => m.id === item.loading_method_id)?.name ?? ''
      : '';
    if (loadingMethods.length > 0 && firstLoadingMethodName !== '') {
      const itemIsPalletized = isLoadingMethodPalletized(itemLoadingMethodName);
      if (itemIsPalletized !== blockIsPalletized) return false;
    }
    if (applyFamilyFilter) {
      const itemFamilyId = options!.itemIdToFamilyId![item.id];
      if (itemFamilyId == null || !options!.allowedFamilyIds!.has(itemFamilyId)) return false;
    }
    return true;
  });
}
